import { useState, useEffect, useCallback, useRef } from 'react';
import { generateQuestion } from '../api';
import QuizQuestion from './QuizQuestion';
import supabase from '../lib/supabase';

function LimitModal({ onUpgrade, onDismiss }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    // Save email to waitlist/leads table for drip sequence
    await supabase.from('email_leads').upsert({ email: email.trim(), source: 'daily_limit' }, { onConflict: 'email' });
    setSent(true);
    setSending(false);
  }

  return (
    <div className="limit-modal-backdrop">
      <div className="limit-modal">
        <div className="limit-modal-icon">🎯</div>
        <h2 className="limit-modal-title">You've used your 5 free questions today!</h2>
        <p className="limit-modal-sub">You're building momentum. Come back tomorrow for 5 more free questions — or unlock unlimited access right now.</p>

        {!sent ? (
          <>
            <form className="limit-email-form" onSubmit={handleSubmit}>
              <p className="limit-email-label">Get a reminder tomorrow when your questions reset:</p>
              <div className="limit-email-row">
                <input
                  className="limit-email-input"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
                <button className="limit-email-btn" type="submit" disabled={sending}>
                  {sending ? '…' : 'Remind me'}
                </button>
              </div>
            </form>
            <button className="upgrade-cta" style={{ marginTop: 16, maxWidth: 320 }} onClick={onUpgrade}>
              Unlock unlimited — $9.99/mo
            </button>
            <button className="back-link" onClick={onDismiss}>Maybe tomorrow</button>
          </>
        ) : (
          <>
            <p className="limit-email-thanks">✓ Got it! We'll remind you tomorrow. See you then 📚</p>
            <button className="upgrade-cta" style={{ marginTop: 16, maxWidth: 320 }} onClick={onUpgrade}>
              Can't wait — upgrade now
            </button>
            <button className="back-link" onClick={onDismiss}>I'll come back tomorrow</button>
          </>
        )}
      </div>
    </div>
  );
}

function TopicQuiz({ subject, topic, color, consumeQuestion, onAskAI, onRecordAnswer, onFinish, onUpgrade }) {
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [sessionCorrect, setSessionCorrect] = useState(0);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [done, setDone] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const recentQuestions = useRef([]);

  const loadQuestion = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAnswered(false);
    setQuestion(null);
    try {
      const q = await generateQuestion(subject, topic, recentQuestions.current);
      recentQuestions.current = [...recentQuestions.current, q.question].slice(-8);
      setQuestion(q);
      consumeQuestion();
    } catch (e) {
      const msg = e.message || '';
      if (msg === 'LIMIT_REACHED') {
        setShowLimitModal(true);
      } else if (msg.startsWith('529')) {
        setError('Anthropic is temporarily overloaded. Please wait a moment and retry.');
      } else {
        setError(msg || 'Could not load question. Check your API key or connection.');
      }
    } finally {
      setLoading(false);
    }
  }, [subject, topic, consumeQuestion]);

  const didLoad = useRef(false);
  useEffect(() => {
    if (didLoad.current) return;
    didLoad.current = true;
    loadQuestion();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleAnswer(isCorrect) {
    setAnswered(true);
    setSessionTotal(t => t + 1);
    if (isCorrect) setSessionCorrect(c => c + 1);
    onRecordAnswer(topic, isCorrect, subject);
  }

  function handleNext() {
    loadQuestion();
  }

  function handleFinish() {
    setDone(true);
  }

  if (done) {
    const pct = sessionTotal > 0 ? Math.round((sessionCorrect / sessionTotal) * 100) : 0;
    return (
      <div className="session-done">
        <p className="session-done-icon">{pct >= 70 ? '🎉' : '💪'}</p>
        <h2>Session Complete</h2>
        <p className="session-score">{pct}%</p>
        <p>{sessionCorrect} / {sessionTotal} correct</p>
        <p className="session-msg">{pct >= 70 ? 'Great work! Keep it up.' : 'Keep practicing — you\'re getting there!'}</p>
        <button className="primary-btn" style={{ background: color }} onClick={onFinish}>
          Back to Topics
        </button>
      </div>
    );
  }

  return (
    <div>
      {showLimitModal && (
        <LimitModal
          onUpgrade={() => { setShowLimitModal(false); onUpgrade(); }}
          onDismiss={() => { setShowLimitModal(false); onFinish(); }}
        />
      )}
      <div className="quiz-session-header">
        <span className="session-topic">{topic}</span>
        <span className="session-score-inline">{sessionCorrect}/{sessionTotal}</span>
      </div>

      {loading && (
        <div className="loading-box">
          <div className="spinner" />
          <p>Generating question…</p>
        </div>
      )}

      {error && (
        <div className="error-box">
          <p>{error}</p>
          <button className="primary-btn" style={{ background: color }} onClick={loadQuestion}>Retry</button>
        </div>
      )}

      {question && !loading && (
        <div className="quiz-panel">
          <QuizQuestion
            question={question}
            color={color}
            onAnswer={handleAnswer}
            onAskAI={() => onAskAI(question, subject, topic)}
          />
          {answered && (
            <div className="next-actions">
              <button className="primary-btn" style={{ background: color }} onClick={handleNext}>
                Next Question
              </button>
              <button className="secondary-btn" onClick={handleFinish}>
                Finish Session
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TopicQuiz;
